import * as React from 'react';

import { ContentCard } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue } from 'jotai';

import * as state from '../../../_state/cameraDetail.state';
import { CameraFormState } from '../../../_state/cameraForm.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { CameraDetailRecord } from '../../../types/sidecar';

import { CameraFormFields } from './CameraFormFields';
import { CameraFormFooterActions } from './CameraFormFooterActions';

import { useCameraLocalClient } from '~/shared/providers/libp2p/useCameraLocalClient';

type Props = {
  camera: CameraDetailRecord;
  onDiscard?: () => void;
  onSaveUpdates?: (data?: CameraFormState) => void;
  isSaving?: boolean;
};

export const CameraTabDetails: React.FC<Props> = ({
  camera,
  onDiscard,
  onSaveUpdates,
  isSaving = false,
}) => {
  const t = useTranslations();
  const toast = useToast();

  const isRtsp = camera.driverType === 'rtsp';

  const cameraClient = useCameraLocalClient(isRtsp ? '' : camera.sidecarId);
  const [rtspEnabled, setRtspEnabled] = React.useState<boolean | null>(null);
  const [rtspPort, setRtspPort] = React.useState<number | null>(null);
  const [isEnablingRtsp, setIsEnablingRtsp] = React.useState(false);
  const [httpEnabled, setHttpEnabled] = React.useState<boolean | null>(null);
  const [httpPort, setHttpPort] = React.useState<number | null>(null);
  const [isEnablingHttp, setIsEnablingHttp] = React.useState(false);
  const [onvifEnabled, setOnvifEnabled] = React.useState<boolean | null>(null);
  const [onvifPort, setOnvifPort] = React.useState<number | null>(null);
  const [isEnablingOnvif, setIsEnablingOnvif] = React.useState(false);
  const [phase, setPhase] = React.useState<string | null>(null);

  const { refetch: _refetch } = useAtomValue(state.$remoteCameraDetail);

  const refreshServiceInfo = React.useCallback(async () => {
    if (isRtsp || !cameraClient) {
      setRtspEnabled(null);
      setRtspPort(null);
      setHttpEnabled(null);
      setHttpPort(null);
      setOnvifEnabled(null);
      setOnvifPort(null);
      setPhase(null);
      return;
    }

    try {
      const res = await cameraClient.getCameraInfo({ cameraId: camera.id });
      setRtspEnabled(res.camera?.rtspEnabled ?? null);
      setRtspPort(res.camera?.rtspPort ?? null);
      setHttpEnabled(res.camera?.httpEnabled ?? null);
      setHttpPort(res.camera?.httpPort ?? null);
      setOnvifEnabled(res.camera?.onvifEnabled ?? null);
      setOnvifPort(res.camera?.onvifPort ?? null);
      setPhase(res.camera?.phase ?? null);
    } catch {
      setRtspEnabled(null);
      setRtspPort(null);
      setHttpEnabled(null);
      setHttpPort(null);
      setOnvifEnabled(null);
      setOnvifPort(null);
      setPhase(null);
    }
  }, [isRtsp, cameraClient, camera.id]);

  React.useEffect(() => {
    setRtspEnabled(null);
    setRtspPort(null);
    setHttpEnabled(null);
    setHttpPort(null);
    setOnvifEnabled(null);
    setOnvifPort(null);
    setPhase(null);
    void refreshServiceInfo();
  }, [refreshServiceInfo]);

  const handleEnableRtsp = React.useCallback(async () => {
    if (!cameraClient) return;

    try {
      setIsEnablingRtsp(true);
      const response = await cameraClient.enableCameraRTSP({
        cameraId: camera.id,
      });
      if (!response.success) {
        throw new Error(
          response.errorMessage || t.hardwareView.common.unknownError,
        );
      }
      toast.success(t.hardwareView.cameraDetailView.messages.enableRtspSuccess);
    } catch (err) {
      toast.error(
        t.hardwareView.cameraDetailView.messages.enableRtspError(
          err instanceof Error
            ? err.message
            : t.hardwareView.common.unknownError,
        ),
      );
    } finally {
      await refreshServiceInfo();
      setIsEnablingRtsp(false);
    }
  }, [cameraClient, camera.id, toast, t, refreshServiceInfo]);

  const handleEnableHttp = React.useCallback(async () => {
    if (!cameraClient) return;

    try {
      setIsEnablingHttp(true);
      const response = await cameraClient.enableCameraHTTP({
        cameraId: camera.id,
      });
      if (!response.success) {
        throw new Error(
          response.errorMessage || t.hardwareView.common.unknownError,
        );
      }
      toast.success(t.hardwareView.cameraDetailView.messages.enableHttpSuccess);
    } catch (err) {
      toast.error(
        t.hardwareView.cameraDetailView.messages.enableHttpError(
          err instanceof Error
            ? err.message
            : t.hardwareView.common.unknownError,
        ),
      );
    } finally {
      await refreshServiceInfo();
      setIsEnablingHttp(false);
    }
  }, [cameraClient, camera.id, toast, t, refreshServiceInfo]);

  const handleEnableOnvif = React.useCallback(async () => {
    if (!cameraClient) return;

    try {
      setIsEnablingOnvif(true);
      const response = await cameraClient.enableCameraONVIF({
        cameraId: camera.id,
      });
      if (!response.success) {
        throw new Error(
          response.errorMessage || t.hardwareView.common.unknownError,
        );
      }
      toast.success(
        t.hardwareView.cameraDetailView.messages.enableOnvifSuccess,
      );
    } catch (err) {
      toast.error(
        t.hardwareView.cameraDetailView.messages.enableOnvifError(
          err instanceof Error
            ? err.message
            : t.hardwareView.common.unknownError,
        ),
      );
    } finally {
      await refreshServiceInfo();
      setIsEnablingOnvif(false);
    }
  }, [cameraClient, camera.id, toast, t, refreshServiceInfo]);

  return (
    <div className="@CameraTabDetails flex max-h-full flex-col gap-5 p-5">
      <ContentCard className="p-0 pt-5">
        <CameraFormFields
          camera={camera}
          phase={phase}
          hasCameraClient={!!cameraClient}
          rtspEnabled={rtspEnabled}
          rtspPort={rtspPort}
          httpEnabled={httpEnabled}
          httpPort={httpPort}
          onvifEnabled={onvifEnabled}
          onvifPort={onvifPort}
          onEnableRtsp={handleEnableRtsp}
          onEnableHttp={handleEnableHttp}
          onEnableOnvif={handleEnableOnvif}
          isEnablingRtsp={isEnablingRtsp}
          isEnablingHttp={isEnablingHttp}
          isEnablingOnvif={isEnablingOnvif}
        />
        <CameraFormFooterActions
          onSaveUpdates={onSaveUpdates}
          onDiscard={onDiscard}
          isSaving={isSaving}
        />
      </ContentCard>
    </div>
  );
};
